import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { Toolbar, PaletteFooter } from "@/pages/MoodboardA";

// Route B · "De Volgende Beweging"
// Palette: nachtblauw #1F5132, koraal #E06B3F, off-white #F5F1E8
// Motif: strakke route-paden / contour lines (subway-map)
// Photos: portretten in beweging, duotone, city stride

const PALETTE = {
  bg: "#0F1A2A",
  ink: "#F5F1E8",
  koraal: "#E06B3F",
  navy: "#1F5132",
  crème: "#F5F1E8",
  surface: "#1F5132",
};

const IMGS = {
  loper: "https://images.unsplash.com/photo-1552058544-f2b08422138a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  stad: "https://images.unsplash.com/photo-1519741347686-c1e0aadf4611?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  trap: "https://images.unsplash.com/photo-1499750310107-5fef28a66643?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  portret: "https://images.unsplash.com/photo-1508979215434-fbb1c56aad33?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
};

const ease = [0.22, 1, 0.36, 1];

function RouteLines({ color = PALETTE.koraal, className = "" }) {
  return (
    <svg className={className} viewBox="0 0 1400 300" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      {[0, 1, 2, 3].map((i) => (
        <motion.path
          key={i}
          d={`M 0 ${60 + i * 40} L 300 ${60 + i * 40} L 400 ${100 + i * 40} L 900 ${100 + i * 40} L 1000 ${140 + i * 40} L 1400 ${140 + i * 40}`}
          stroke={i === 1 ? color : PALETTE.ink + "30"}
          strokeWidth={i === 1 ? "2.5" : "1"}
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 2.5, delay: i * 0.15, ease }}
        />
      ))}
      {[300, 900, 1400].map((x, i) => (
        <motion.circle
          key={x}
          cx={x}
          cy={i === 2 ? 180 : i === 1 ? 140 : 100}
          r="7"
          fill={PALETTE.bg}
          stroke={color}
          strokeWidth="2.5"
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 1 + i * 0.2 }}
        />
      ))}
    </svg>
  );
}

function HeroDeelnemer() {
  return (
    <section className="relative overflow-hidden" style={{ background: PALETTE.bg, color: PALETTE.ink }}>
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pt-20 pb-28 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.4em]" style={{ color: PALETTE.koraal }}>
              Editie 01 · Voor jou
            </p>
            <h1 className="font-heading font-light text-[52px] sm:text-7xl lg:text-[104px] leading-[0.94] tracking-tight mt-10">
              De volgende
              <br />
              <span className="italic" style={{ color: PALETTE.koraal }}>beweging</span>
              <br />
              begint klein.
            </h1>
            <p className="mt-10 max-w-xl text-lg leading-relaxed opacity-85">
              Eén eerste stap. Twaalf maanden ruimte. Een fase eerder dan een traject — daar waar het écht begint.
            </p>
            <div className="mt-12 flex items-center gap-5">
              <button className="group inline-flex items-center gap-4 rounded-none border pl-7 pr-2 py-2 transition-all hover:bg-white/5" style={{ borderColor: PALETTE.ink + "50" }}>
                <span className="text-sm uppercase tracking-[0.2em] font-medium">Zelfscan</span>
                <span className="w-10 h-10 flex items-center justify-center" style={{ background: PALETTE.koraal }}>
                  <ArrowUpRight size={16} color={PALETTE.bg} />
                </span>
              </button>
              <span className="text-xs uppercase tracking-[0.3em] opacity-60">03 min</span>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-none overflow-hidden">
              <img src={IMGS.loper} alt="In beweging" className="w-full h-full object-cover" style={{ filter: "grayscale(30%) contrast(1.1)" }} />
              <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 50%, ${PALETTE.bg}b0)` }} />
              <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.3em] opacity-80">FIG. 01</div>
              <div className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.3em] opacity-80">33°N — vooruit</div>
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.9, delay: 0.6, ease }}
                className="absolute bottom-6 left-6 right-6 border-t pt-4"
                style={{ borderColor: PALETTE.ink + "40" }}
              >
                <p className="font-heading italic text-xl leading-tight">
                  "Ik hoefde niet eerst iemand anders te worden."
                </p>
                <p className="mt-2 text-[10px] uppercase tracking-[0.3em] opacity-60">— Youssef, 24</p>
              </motion.div>
            </div>
          </div>
        </div>

        <div className="mt-24">
          <RouteLines className="w-full h-40" />
          <div className="mt-4 grid grid-cols-3 text-[10px] uppercase tracking-[0.3em] opacity-70">
            <span>Startpunt</span>
            <span className="text-center">LevelMoment</span>
            <span className="text-right">Volgende level</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroOpdrachtgever() {
  return (
    <section className="relative overflow-hidden" style={{ background: PALETTE.crème, color: PALETTE.navy }}>
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pt-24 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.4em]" style={{ color: PALETTE.koraal }}>
              Editie 01 · Voor gemeenten
            </p>
            <h2 className="font-heading font-light text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight mt-8">
              Een fase eerder.
              <br />
              <span className="italic" style={{ color: PALETTE.koraal }}>Meetbaar</span>, met bewijs.
            </h2>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed opacity-85">
              LevelLeven activeert het onbenutte deel van uw bestand — vóór de klantmanager, ná de indicatie. Twaalf maanden, drie metingen, één warm overdrachtsmoment.
            </p>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-2xl border-t pt-8" style={{ borderColor: PALETTE.navy + "20" }}>
              {[
                { k: "01", t: "Nulmeting", d: "Vertrekpunt zichtbaar" },
                { k: "02", t: "6 maanden", d: "Belastbaarheid + 34%" },
                { k: "03", t: "12 maanden", d: "Warm overdragen" },
              ].map((s) => (
                <div key={s.k}>
                  <p className="chapter-num text-5xl" style={{ color: PALETTE.koraal }}>{s.k}</p>
                  <p className="text-xs uppercase tracking-[0.2em] mt-2">{s.t}</p>
                  <p className="text-sm mt-1 opacity-70">{s.d}</p>
                </div>
              ))}
            </div>
            <div className="mt-12 flex items-center gap-5">
              <button className="group inline-flex items-center gap-4 rounded-none pl-7 pr-2 py-2 transition-all" style={{ background: PALETTE.navy, color: PALETTE.crème }}>
                <span className="text-sm uppercase tracking-[0.2em] font-medium">Plan pilot-gesprek</span>
                <span className="w-10 h-10 flex items-center justify-center" style={{ background: PALETTE.koraal }}>
                  <ArrowUpRight size={16} color={PALETTE.crème} />
                </span>
              </button>
              <span className="text-xs uppercase tracking-[0.3em] opacity-60">Business case 15 min</span>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={IMGS.stad} alt="Stad in beweging" className="w-full h-full object-cover" style={{ filter: "contrast(1.05) saturate(0.85)" }} />
            </div>
            <div className="mt-3 flex justify-between text-[10px] uppercase tracking-[0.3em] opacity-60">
              <span>FIG. 02 — Zichtbare beweging</span>
              <span>2026 —</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function MoodboardB() {
  const [tab, setTab] = useState("deelnemer");
  usePageMeta({ title: "Route B · De Volgende Beweging · Moodboard" });

  return (
    <div data-testid="moodboard-b" style={{ background: PALETTE.bg }}>
      <Toolbar current="b" tab={tab} setTab={setTab} palette={PALETTE} />
      {tab === "deelnemer" ? <HeroDeelnemer /> : <HeroOpdrachtgever />}
      <PaletteFooter palette={PALETTE} name="De Volgende Beweging" font='"Cormorant Garamond" italic + "Manrope"' />
    </div>
  );
}
